import React, {useContext, useMemo, useRef, useState} from "react"
import AppContext from "../../appContext"
import LimeProgramLabInputRow from "../../controllers/LimeProgramLabInputRow"
import LimeProgramLabInputs from "../../controllers/LimeProgramLabInputs"
import LabInputUnit from "../../controllers/LabInputUnit"
import {useBoundaries} from "../../controllers/useBoundaries"
import {boundariesToRings} from "../../controllers/boundaryGeo"
import {DataTable} from "../../components/DataTable"
import {PlusCircleIcon, ArrowUpOnSquareIcon, MapIcon} from "@heroicons/react/24/solid"
import {arrayPush, arrayRemoveIndex, arrayUpdate} from "../../immutableState"
import Dialog from "../../components/Dialog"
import FileUploader from "../../components/FileUploader"
import SampleLocationsDialog from "./SampleLocationsDialog"
import {parseCSVFile} from "../program/CSVParse"
import {
    RowWithId,
    createEmptyRow,
    isMeq100g,
    formatValue,
    convertRowForEditing,
    convertRowForSaving,
    buildLimeSoilColumnMatchers,
    buildSoilDisplayColumns,
    rowsToSamplePoints
} from "./limeSoilUtils"
import LimeSoilRowEditor from "./LimeSoilRowEditor"

interface LimeSoilDataDialogProps {
    data: LimeProgramLabInputs
    onSave: (data: LimeProgramLabInputs) => void
    onDiscard: () => void
    labInputUnit?: LabInputUnit
    landIds?: number[]
}

let nextId = -1
function getNextId(): number {
    return nextId--
}

const LimeSoilDataDialog: React.FC<LimeSoilDataDialogProps> = (props) => {
    const app = useContext(AppContext)
    const system = app.initial.system
    const [data, setData] = useState<RowWithId[]>(() =>
        props.data.rows.map((row, index) => ({...row, _id: index}))
    )
    const [showUpload, setShowUpload] = useState(false)
    const [showEdit, setShowEdit] = useState(false)
    const [editRow, setEditRow] = useState<RowWithId>(createEmptyRow(0))
    const [editIndex, setEditIndex] = useState<number>(-1)
    const changed = useRef<boolean>(false)
    const [showMap, setShowMap] = useState(false)

    // Use the useBoundary hook instead of manual useEffect
    const {geometryMap, loading: boundaryLoading, error: boundaryError} = useBoundaries(props.landIds)
    const multipleBoundaries = React.useMemo(() => boundariesToRings(Object.values(geometryMap)), [geometryMap])

    // Memoize sample points to avoid recalculating on every render
    const samplePoints = useMemo(() => rowsToSamplePoints(data), [data])

    // meq/100g regions use different unit for K, Na, Ca, Mg; other regions use mg/kg
    const meq100gMode = isMeq100g(props.labInputUnit)

    function handleCsvUpload(file: File) {
        const reader = new FileReader()
        reader.onload = (e) => {
            const text = e.target?.result as string
            const columnMatchers = buildLimeSoilColumnMatchers(meq100gMode)
            const rows = parseCSVFile<RowWithId>(
                text,
                () => createEmptyRow(getNextId()),
                system,
                columnMatchers
            )
            setData(prev => [...prev, ...rows])
            changed.current = true
            setShowUpload(false)
        }
        reader.readAsText(file)
    }

    function addRow() {
        setEditRow(createEmptyRow(getNextId()))
        setEditIndex(-1)
        setShowEdit(true)
    }

    function onEdit(row: RowWithId, index: number) {
        setEditRow(convertRowForEditing(row, meq100gMode))
        setEditIndex(index)
        setShowEdit(true)
    }

    function onRemove(index: number) {
        setData(arrayRemoveIndex(data, index))
        changed.current = true
    }

    function saveEdit() {
        const rowToSave = convertRowForSaving(editRow, meq100gMode)

        if (editIndex === -1) {
            setData(arrayPush(data, rowToSave))
        } else {
            setData(arrayUpdate(data, editIndex, rowToSave))
        }
        changed.current = true
        setShowEdit(false)
    }

    return <>
        <Dialog
            title={app.word('upload_csv')}
            show={showUpload}
            setShow={setShowUpload}
            body={
                <div className="p-4">
                    <div>{app.word('download_csv_sample')}, {app.word('add_data_and_upload_again')}.</div>
                    <a className="underline" href="/LimeSoilSample.csv" download="LimeSoilSample.csv">LimeSoilSample.csv</a>
                    <div className="my-2">
                        <FileUploader onChange={file => handleCsvUpload(file)} fileTypes=".csv"/>
                    </div>
                </div>
            }
        />

        <Dialog
            title={editIndex === -1 ? app.word('add') : app.word('edit')}
            show={showEdit}
            setShow={setShowEdit}
            body={
                <div className="p-4 max-h-[70vh] overflow-y-auto">
                    <LimeSoilRowEditor editRow={editRow} setEditRow={setEditRow} system={system} meq100gMode={meq100gMode}/>

                    <div className="flex justify-end items-end">
                        <button className="btn-sm bg-red-500" onClick={() => setShowEdit(false)}>
                            {app.word('discard')}
                        </button>
                        <button className="btn-sm bg-primary" onClick={saveEdit}>
                            {app.word('save')}
                        </button>
                    </div>
                </div>
            }
        />

        <SampleLocationsDialog
            show={showMap}
            setShow={setShowMap}
            rows={data}
            samplePoints={samplePoints}
            multipleBoundaries={multipleBoundaries}
            boundaryLoading={boundaryLoading}
            boundaryError={boundaryError}
            meq100gMode={meq100gMode}
        />

        <div className="my-2">
            {data.length > 0 ? (
                <>
                    <div className="text-xl text-primary mb-2">{app.word('soil_data')}</div>
                    <div className="overflow-x-auto">
                        <DataTable
                            headerClass="px-2 py-2 text-left text-xs font-semibold bg-gray-200"
                            dataClass="whitespace-nowrap px-1 py-2 text-xs text-gray-600"
                            keyExtractor={(row: RowWithId) => row._id}
                            definitions={[
                                // Columns follow the canonical ITEST Soil Export / Online Liming template order.
                                {header: app.word('block'), row: r => r.landName || '—'},
                                {header: 'Sample Number', row: r => r.referenceNr},
                                {header: 'Lab number', row: r => r.labNumber || '—'},
                                {header: 'Latitude', row: r => formatValue(r.latitude)},
                                {header: 'Longitude', row: r => formatValue(r.longitude)},
                                {header: 'Sample depth', row: r => app.word(r.sampleDepth)},
                                ...buildSoilDisplayColumns(system, meq100gMode).map(c => ({header: c.header, row: c.value})),
                                {
                                    header: app.word('actions'),
                                    row: (r, index) => <div>
                                        <span className="underline cursor-pointer text-primary"
                                              onClick={() => onRemove(index)}>{app.word('remove')}</span>
                                        <span className="px-2">|</span>
                                        <span className="underline cursor-pointer text-primary"
                                              onClick={() => onEdit(r, index)}>{app.word('edit')}</span>
                                    </div>
                                }
                            ]}
                            data={data}
                        />
                    </div>
                </>
            ) : (
                <div>{app.word('no_soil_data')}</div>
            )}

            <div className="flex items-center mt-2">
                <span title={app.word('add')} className="mr-2">
                    <PlusCircleIcon className="w-6 h-6 cursor-pointer" onClick={addRow}/>
                </span>
                <span title={app.word('upload_csv')} className="mr-2">
                    <ArrowUpOnSquareIcon className="w-6 h-6 cursor-pointer" onClick={() => setShowUpload(true)}/>
                </span>
                {(samplePoints.length > 0 || multipleBoundaries.length > 0 || (props.landIds && props.landIds.length > 0)) && (
                    <span title={app.word('view_map')}>
                        <MapIcon
                            className={`w-6 h-6 cursor-pointer ${boundaryLoading ? 'text-gray-400 animate-pulse' : 'text-primary'}`}
                            onClick={() => setShowMap(true)}
                        />
                    </span>
                )}
            </div>
        </div>

        <div className="border-t mt-4 pt-4">
            <div className="flex justify-end">
                <button className="btn-sm bg-red-500" onClick={() => props.onDiscard()}>
                    {app.word('discard')}
                </button>
                <button className="btn-sm bg-primary" onClick={() => {
                    if (changed.current) {
                        // Strip out the _id field before saving
                        const rowsWithoutId: LimeProgramLabInputRow[] = data.map(({_id, ...row}) => row)
                        props.onSave({rows: rowsWithoutId})
                    } else {
                        props.onDiscard()
                    }
                }}>
                    {app.word('save')}
                </button>
            </div>
        </div>
    </>
}

export default LimeSoilDataDialog
